import {
  LegacyCanvasTo3DDragConverterSystem,
  AnyDragSurfaceProjector
} from '@inter-ikea-core/core';
import { Feature, UsesViewer } from '../../../features/Feature';
import {
  CoreApi,
  FadeAnimationSystem,
  FadeMaterialSystem,
  HideOccludingObjectsSystem,
  WallVisibilitySystem,
  WallVisibilityValidation
} from '@inter-ikea-storageone/range';
import { getRoomChildren, hasKvadratComponent } from '@inter-ikea-kvadrat/kvadrat';

/**
 * Feature that handles see-through functionality for multiple walls.
 * Sets up systems for wall visibility, hiding occluding objects, and fade animations.
 */
export class MultiWallSeeThroughFeature extends Feature implements UsesViewer {
  /**
   * Initializes systems needed for wall visibility and object fading when viewer is created
   * @param coreApi - Core API instance providing access to services and systems
   */
  onViewerCreated(coreApi: CoreApi): void {
    const wallVisibilitySystem = new WallVisibilitySystem(coreApi);
    coreApi.registerSystem(wallVisibilitySystem);
    wallVisibilitySystem.init();
    coreApi.registerValidation(new WallVisibilityValidation(coreApi));

    const hideOccludingObjectsSystem = new HideOccludingObjectsSystem(coreApi.getServices(), () => {
      const roomChildrenExceptKvadratEntities = getRoomChildren(
        coreApi.getServices().sceneManager
      ).filter(entity => !hasKvadratComponent(entity));
      return roomChildrenExceptKvadratEntities;
    });
    coreApi.registerSystem(hideOccludingObjectsSystem);
    hideOccludingObjectsSystem.enable();

    const fadeMaterialSystem = new FadeMaterialSystem(coreApi.getServices());
    coreApi.registerSystem(fadeMaterialSystem);
    fadeMaterialSystem.enable();

    const fadeAnimationSystem = new FadeAnimationSystem(coreApi.getServices());
    coreApi.registerSystem(fadeAnimationSystem);
    fadeAnimationSystem.enable();
  }

  /**
   * Initializes drag conversion system
   * @param coreApi - Core API instance providing access to services
   * @protected
   */
  protected init(coreApi: CoreApi): void {
    coreApi.registerSystem(
      new LegacyCanvasTo3DDragConverterSystem(
        coreApi.getServices().entityManager,
        coreApi.getServices().sceneManager,
        new AnyDragSurfaceProjector(
          coreApi.getServices().sceneManager,
          coreApi.getServices().entityManager
        )
      )
    );
  }
}
